import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { LuPencil, LuTrash2 } from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";

import { LottieLoading } from "../../../components/graphics/LottieLoading";
import {
  IPayment,
  useDeletePaymentMutation,
  useGetPaymentByIdQuery,
  useGetPaymentStatusesQuery,
  useSetPaymentLabelMutation,
} from "../../../services/BillingService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { navigateToContact } from "../../contacts/utils/navigateToContacts";
import { formatBillingDate } from "../billingDates.utils";
import DeletePopup from "../DeletePopup";
import DropStatus from "../DropStatus";
import {
  ActionsMenu,
  BILLING_CARD,
  BILLING_FIELD_LABEL,
  BILLING_SECONDARY_BUTTON,
  ClientAvatar,
  formatMoney,
} from "../ui/BillingUi";

type PaymentClient = {
  id?: string | null;
  name?: string | null;
  contactId?: string | null;
  businessContactId?: string | null;
};

/** Saved payment page (V49 look). Edit opens the New payment form pre-filled (PAY-018). */
const PaymentDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  const { data: payment, isLoading: isLoadingPayment } = useGetPaymentByIdQuery(
    typeof id === "string" && id !== "create" ? id : "",
  );

  const { data: statuses, isLoading: isLoadingStatuses } =
    useGetPaymentStatusesQuery(getLinkedFirm()?.orgId || "");

  const [deletePayment, { isSuccess: isSuccessDelete }] =
    useDeletePaymentMutation();

  const [updatePayment, { isLoading: isLoadingUpdate }] =
    useSetPaymentLabelMutation();

  // Only the label changes (QA batch 12).
  const updateStatus = (statusId: string) => {
    updatePayment({ id: payment?.id || "", labelId: statusId || null });
  };

  useEffect(() => {
    if (isSuccessDelete) {
      navigate("/billing/payments");
    }
  }, [isSuccessDelete]);

  if (isLoadingPayment || isLoadingStatuses) {
    return <LottieLoading />;
  }

  if (!payment) {
    return null;
  }

  const total = (payment.paymentItem || []).reduce(
    (sum, item) => sum + (item?.amountPaid || 0),
    0,
  );

  const openEdit = () => navigate(`/billing/payments/${payment.id}/edit`);

  const clients: PaymentClient[] = [
    {
      id: payment.customer?.id || payment.customerId,
      name: payment.customer?.name,
      contactId: payment.customer?.contactId,
      businessContactId: payment.customer?.businessContactId,
    },
    ...(payment.paymentClients || []).filter(
      (client) => client?.id !== (payment.customer?.id || payment.customerId),
    ),
  ];

  const openClient = (client: PaymentClient) =>
    navigate(
      navigateToContact({
        type: client.businessContactId ? "organization" : "contact",
        customerId: client.id || "",
        id: client.businessContactId || client.contactId || "",
      }),
    );

  return (
    <div className={classNames(BILLING_CARD, "px-7 py-[26px]")}>
      <div className={"mb-6 flex items-start justify-between gap-4"}>
        <div>
          <div className={BILLING_FIELD_LABEL}>Payment</div>
          <h2
            className={
              "m-0 mt-1 text-2xl font-extrabold tracking-[-0.5px] text-[#1F2433]"
            }
          >
            {formatMoney(payment.amount ?? total)}
          </h2>
        </div>
        <div className={"flex items-center gap-2.5"}>
          <DropStatus
            invoiceStatus={null}
            isLoading={isLoadingUpdate || isLoadingPayment}
            paymentStatus={payment.paymentStatus || null}
            statuses={statuses || []}
            selectStatus={(status) => {
              updateStatus(status?.id || "");
            }}
          />
          <button
            type={"button"}
            onClick={openEdit}
            className={classNames(BILLING_SECONDARY_BUTTON, "h-[34px] px-4")}
          >
            <LuPencil size={14} />
            Edit
          </button>
          <ActionsMenu
            items={[
              {
                label: "Edit",
                icon: <LuPencil size={15} />,
                onClick: openEdit,
              },
              {
                label: "Delete",
                icon: <LuTrash2 size={15} />,
                danger: true,
                onClick: () => setIsOpenDelete(true),
              },
            ]}
          />
        </div>
      </div>

      <div className={"mb-6 flex flex-wrap gap-x-10 gap-y-4"}>
        <div>
          <div className={classNames(BILLING_FIELD_LABEL, "mb-2")}>Client</div>
          <div className={"flex flex-col gap-2"}>
            {clients.map((client, index) => (
              <button
                key={client.id || index}
                type={"button"}
                onClick={() => openClient(client)}
                className={"flex items-center gap-2.5 text-left"}
              >
                <ClientAvatar
                  client={{
                    id: client.id || "",
                    name: client.name || "",
                    isOrganization: !!client.businessContactId,
                  }}
                  size={26}
                />
                <span
                  className={
                    "text-[13.5px] font-semibold text-[#6B4EFF] hover:underline"
                  }
                >
                  {client.name}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className={classNames(BILLING_FIELD_LABEL, "mb-2")}>
            Payment date
          </div>
          <div className={"text-[13.5px] text-[#1F2433]"}>
            {formatBillingDate(payment.paymentDate)}
          </div>
        </div>
        <div>
          <div className={classNames(BILLING_FIELD_LABEL, "mb-2")}>
            Payment method
          </div>
          <div className={"text-[13.5px] text-[#1F2433]"}>
            {payment.paymentMethod?.paymentMethod || "—"}
          </div>
        </div>
      </div>

      <div className={"overflow-hidden rounded-xl border border-[#EEF0F3]"}>
        <div
          className={
            "grid grid-cols-[1fr_1fr_1fr_1fr_1fr] gap-2.5 border-b border-[#EEF0F3] bg-[#FAFBFC] px-[18px] py-3 text-[12.5px] font-bold text-[#8B92A2]"
          }
        >
          <span>Invoice #</span>
          <span>Due date</span>
          <span>Original amount</span>
          <span>Balance after payment</span>
          <span className={"text-right"}>Amount paid</span>
        </div>
        {(payment.paymentItem || []).map((item, index) => (
          <button
            type={"button"}
            key={item?.id || index}
            onClick={() => navigate(`/billing/invoices/${item?.invoiceId}`)}
            className={
              "grid w-full grid-cols-[1fr_1fr_1fr_1fr_1fr] gap-2.5 border-b border-[#F4F5F7] px-[18px] py-[13px] text-left text-[13.5px] text-[#3A4256] last:border-b-0 hover:bg-[#FAFBFC]"
            }
          >
            <span className={"font-semibold text-[#6B4EFF]"}>
              {item?.invoiceNo}
            </span>
            <span>{formatBillingDate(item?.invoiceDueDate)}</span>
            <span className={"tabular-nums"}>
              {formatMoney(item?.invoiceTotal)}
            </span>
            <span className={"tabular-nums"}>
              {formatMoney(item?.invoiceBalance)}
            </span>
            <span className={"text-right font-semibold tabular-nums"}>
              {formatMoney(item?.amountPaid)}
            </span>
          </button>
        ))}
      </div>

      <div
        className={"mt-[22px] flex flex-wrap items-start justify-between gap-4"}
      >
        <div className={"min-w-[260px] max-w-[500px] flex-1"}>
          <div className={classNames(BILLING_FIELD_LABEL, "mb-2")}>Notes</div>
          <div
            className={
              "min-h-[70px] whitespace-pre-wrap break-words rounded-[10px] border border-[#EAECEF] bg-[#FAFBFC] px-3.5 py-3 text-[13px] text-[#1F2433]"
            }
          >
            {payment.notes || (
              <span className={"text-[#9AA0B0]"}>No notes</span>
            )}
          </div>
        </div>
        <div
          className={
            "flex items-center gap-4 rounded-[10px] border border-[#E6E0FF] bg-[#F7F5FF] px-[18px] py-3"
          }
        >
          <span className={"text-[13px] font-bold text-[#3A4256]"}>
            Amount paid
          </span>
          <span
            className={"text-lg font-extrabold tabular-nums text-[#6B4EFF]"}
          >
            {formatMoney(total)}
          </span>
        </div>
      </div>

      {isOpenDelete && (
        <DeletePopup
          isActive={isOpenDelete}
          title={"Delete this payment?"}
          messages={[
            "This will delete the payment permanently and cannot be undone.",
            "The invoices it paid will show their balance again.",
          ]}
          onClose={() => setIsOpenDelete(false)}
          onDelete={() => {
            setIsOpenDelete(false);
            void deletePayment({ id: payment.id } as IPayment);
          }}
        />
      )}
    </div>
  );
};

export default PaymentDetails;
